import { useEffect, useState } from "react";
import { Plus, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { YEARS, ZONE_LABELS, monthKey } from "@/lib/format";
import { TaxBreakdown } from "@/components/TaxBreakdown";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function CalculatorPage() {
  const { company, companyId, year } = useCompany();
  const [params, setParams] = useState({ annee: year, mois: 12, is_association: false, rapport_assujettissement: 100, zone: "metropole" });
  const [remus, setRemus] = useState([36000]);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      api.post("/calc/simulate", { ...params, rapport_assujettissement: Number(params.rapport_assujettissement) || 0, remunerations: remus.map((r) => Number(r) || 0) })
        .then((r) => setResult(r.data)).catch((e) => toast.error(apiError(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [params, remus]);

  const loadCompany = async () => {
    try {
      const { data } = await api.get(`/companies/${companyId}/employees`);
      const list = data.map((e) => Array.from({ length: 12 }, (_, i) => Number(e.remunerations?.[monthKey(params.annee, i + 1)] || 0)).reduce((a, b) => a + b, 0));
      if (!list.length) return toast.info("Aucun salarié dans cette entreprise");
      setRemus(list);
      setParams((p) => ({ ...p, mois: 12, is_association: company.is_association, rapport_assujettissement: company.rapport_assujettissement, zone: company.zone }));
      toast.success(`${list.length} salarié(s) chargé(s) depuis ${company.name}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const set = (k, v) => setParams((p) => ({ ...p, [k]: v }));

  return (
    <div className="space-y-6" data-testid="calculator-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="eyebrow">Simulation instantanée</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground mt-1">Simulateur</h1>
        </div>
        {company && <Button variant="outline" onClick={loadCompany} data-testid="calculator-load-company-button"><Download className="h-4 w-4 mr-2" /> Charger les salariés de {company.name}</Button>}
      </div>

      <div className="grid lg:grid-cols-[360px_1fr] gap-6">
        <div className="bg-card border border-border rounded-md p-5 space-y-4 h-fit">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Barème</Label>
              <Select value={String(params.annee)} onValueChange={(v) => set("annee", Number(v))}>
                <SelectTrigger className="font-mono" data-testid="tax-calculator-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Période (mois)</Label>
              <Select value={String(params.mois)} onValueChange={(v) => set("mois", Number(v))}>
                <SelectTrigger className="font-mono" data-testid="tax-calculator-months-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 mois</SelectItem>
                  <SelectItem value="3">3 mois (trimestre)</SelectItem>
                  <SelectItem value="12">12 mois (année)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Rapport d'assujettissement (%)</Label>
            <Input type="number" min="0" max="100" step="0.01" className="font-mono" value={params.rapport_assujettissement} onChange={(e) => set("rapport_assujettissement", e.target.value)} data-testid="tax-calculator-assujettissement-input" />
          </div>
          <div>
            <Label>Zone</Label>
            <Select value={params.zone} onValueChange={(v) => set("zone", v)}>
              <SelectTrigger data-testid="tax-calculator-zone-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ZONE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3 rounded-md border border-border p-3 bg-muted/60">
            <Switch checked={params.is_association} onCheckedChange={(v) => set("is_association", v)} data-testid="tax-calculator-association-switch" />
            <span className="text-sm text-foreground">Association loi 1901 (abattement)</span>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Rémunérations brutes ({params.mois} mois)</Label>
              <Button size="sm" variant="ghost" onClick={() => setRemus([...remus, 0])} data-testid="tax-calculator-add-salary-button"><Plus className="h-4 w-4 mr-1" /> Salarié</Button>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {remus.map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-6 font-mono">{i + 1}</span>
                  <Input type="number" min="0" className="font-mono h-9" value={r} onChange={(e) => setRemus(remus.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`tax-calculator-salary-input-${i}`} />
                  <Button size="icon" variant="ghost" className="h-9 w-9 text-muted-foreground/70 hover:text-red-600 dark:text-red-400" disabled={remus.length === 1} onClick={() => setRemus(remus.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>{result ? <TaxBreakdown result={result} /> : <div className="text-muted-foreground">Calcul…</div>}</div>
      </div>
    </div>
  );
}
